import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  AtsJobPostingStatusesEnum,
  AtsPublishJobPostingDocument,
  AtsPublishJobPostingFragment,
} from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { JobPostingMissingFieldsDialog } from 'modules/ats/components/job_postings/PublishJobPostingModal/JobPostingMissingFieldsDialog'
import { isJobOpen } from 'modules/ats/lib/utils/jobPosting'
import { useJobPostingPreview } from 'modules/ats/lib/utils/useJobPostingPreview'

import { PublishModalOptions } from './PublishModalOptions'

interface Form {
  allowInternalApply: boolean
  allowReferral: boolean
  status: AtsJobPostingStatusesEnum
}

interface Props {
  job: AtsPublishJobPostingFragment
  onClose: () => void
  onSuccess: (value: AtsJobPostingStatusesEnum) => void
}

export const PublishJobPostingModal = ({ job, onClose, onSuccess }: Props) => {
  const getPreviewUrl = useJobPostingPreview({ id: job.id, status: job.status })

  const wasInDraft = job.status === 'draft'

  const hasDescription = job.description && job.description.length > 0

  const [publish] = useMutation(AtsPublishJobPostingDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const form = useForm<Form>({
    defaultValues: {
      allowInternalApply: job.allowInternalApply ?? false,
      allowReferral: job.allowReferral ?? false,
      status: job.status === 'published' ? 'unlisted' : 'published',
    },
    async onSubmit(values, { setErrors }) {
      const result = await publish({
        variables: {
          id: job.id,
          allowInternalApply: values.allowInternalApply,
          allowReferral: values.allowReferral,
          status: values.status,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (wasInDraft) {
        track(trackingEvents.ats.publishJobOpeningSubmit, {
          is_successful: handled.success,
          error_code: !handled.success && result.toString(),
          target_job_status: values.status,
        })
      }

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        dispatch({
          type: EventTypes.JOB_POSTING_PUBLISHED,
        })

        onSuccess(values.status)
      }

      return handled
    },
  })

  const missingFieldsErrors = () => {
    const missingFields: string[] = []

    if (!hasDescription) {
      missingFields.push(
        i18n.t('ats.job_details.job_posting_missing_fields_dialog.description_field')
      )
    }

    if (!job.location?.id) {
      missingFields.push(i18n.t('ats.job_details.job_posting_missing_fields_dialog.location_field'))
    }

    return missingFields
  }

  const missingFields = missingFieldsErrors()

  const modalTitle =
    job.status === 'published'
      ? i18n.t('ats.job_details.publish_confirmation.unlist.modal_title')
      : i18n.t('ats.job_details.publish_confirmation.modal_title')

  const description =
    job.status === 'published'
      ? i18n.t('ats.job_details.publish_confirmation.unlist.description')
      : job.status === 'unlisted'
      ? i18n.t('ats.job_details.publish_confirmation.publish.description')
      : i18n.t('ats.job_details.publish_confirmation.description_copy')

  const submit =
    job.status === 'published'
      ? i18n.t('ats.job_details.publish_confirmation.unlist.submit')
      : job.status === 'unlisted'
      ? i18n.t('ats.job_details.publish_confirmation.publish.submit')
      : i18n.t('ats.job_details.publish_confirmation.submit')

  const onCloseModal = async () => {
    await track(trackingEvents.ats.publishJobOpeningCancel, {
      location_id: job.location?.id,
      description: job.description,
    })

    onClose()
  }

  const onClickPreview = async () => {
    const previewUrl = await getPreviewUrl()

    window.open(previewUrl, '_blank')
  }

  if (!hasDescription || missingFields.length > 0) {
    return (
      <JobPostingMissingFieldsDialog
        onClose={onCloseModal}
        missingFields={missingFields}
        id={job.id}
      />
    )
  }

  return (
    <F0Dialog
      onClose={onCloseModal}
      isOpen
      title={modalTitle}
      primaryAction={{
        label: submit,
        onClick: () => form.handleSubmit(),
      }}
      secondaryAction={{
        label: i18n.t('ats.job_details.preview_job'),
        onClick: () => onClickPreview(),
      }}
    >
      <Box gap="s8" textAlign="center" paddingBottom="s32">
        <Text size="400" color="textPrimary" weight="medium">
          {i18n.t('ats.job_details.publish_confirmation.title_copy')}
        </Text>
        <Text.Normal>{description}</Text.Normal>
      </Box>
      <Box gap="s16">
        {!isJobOpen(job.status) && (
          <PublishModalOptions
            onChange={(value) => form.setFieldValue('status', value)}
            currentStatus={form.values.status}
          />
        )}

        <Box gap="s16">
          <Checkbox
            checked={form.values.allowReferral}
            label={i18n.t(
              'ats.job_details.publish_confirmation.open_positions.allow_referral_label'
            )}
            onChange={(event) => form.setFieldValue('allowReferral', event.target.checked)}
          />
          <Box flexDirection="row" gap="s12" alignItems="center">
            <Checkbox
              checked={form.values.allowInternalApply}
              label={i18n.t(
                'ats.job_details.publish_confirmation.open_positions.allow_internal_apply_label'
              )}
              onChange={(event) => form.setFieldValue('allowInternalApply', event.target.checked)}
            />
            <InformationTooltip
              information={i18n.t(
                'ats.job_details.publish_confirmation.open_positions.allow_internal_apply_tooltip'
              )}
            />
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}
